import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { LanguageSwitcher } from "../layout/LanguageSwitcher";
import { PublicLinks } from "./public/PublicPage";

const schema = z.object({
  // z.email() statt z.string().email(): in zod 4 ist die Pruefung am String
  // veraltet. Die Laengenpruefung bleibt, damit ein leeres Feld als leer gemeldet
  // wird und nicht bloss als unguelige Adresse.
  email: z.email().min(1),
  password: z.string().min(1),
});

type LoginForm = z.infer<typeof schema>;

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);

  const { control, handleSubmit, formState } = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null);
    try {
      await login(email, password);
      const target = (location.state as { from?: string } | null)?.from ?? "/";
      navigate(target, { replace: true });
    } catch (error) {
      // Devise answers 401 with its own localised message; anything else is
      // unexpected and shown verbatim rather than hidden behind a generic text.
      setFormError(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : String(error),
      );
    }
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "#f5f5f5",
      }}
    >
      <Card style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            myspeechplan
          </Typography.Title>
          <LanguageSwitcher />
        </div>

        <Form layout="vertical" onFinish={onSubmit} style={{ marginTop: 24 }}>
          {formError && (
            <Alert type="error" title={formError} style={{ marginBottom: 16 }} showIcon />
          )}

          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <Form.Item
                label={t("activerecord.attributes.user.email")}
                validateStatus={fieldState.error ? "error" : undefined}
                // errors.messages.* are Rails' sentence fragments ("ist nicht
                // gültig"); they only read as a sentence with the attribute in
                // front, which is how ActiveRecord composes them too.
                help={
                  fieldState.error
                    ? `${t("activerecord.attributes.user.email")} ${t("errors.messages.invalid")}`
                    : undefined
                }
              >
                <Input {...field} type="email" autoComplete="username" autoFocus />
              </Form.Item>
            )}
          />

          <Controller
            control={control}
            name="password"
            render={({ field, fieldState }) => (
              <Form.Item
                label={t("activerecord.attributes.user.password")}
                validateStatus={fieldState.error ? "error" : undefined}
                help={
                  fieldState.error
                    ? `${t("activerecord.attributes.user.password")} ${t("errors.messages.blank")}`
                    : undefined
                }
              >
                <Input.Password {...field} autoComplete="current-password" />
              </Form.Item>
            )}
          />

          <Button type="primary" htmlType="submit" block loading={formState.isSubmitting}>
            {/* Not user.login_as — that is "log in with user rights", the
                administrator's impersonation action. */}
            {t("auth.sign_in")}
          </Button>
        </Form>

        {/* The four ways past a login that will not work — registration, a
            forgotten password, a missing confirmation, a locked account. The
            old application offered all of them under its form; without them
            there is no way back in but a phone call to a coordinator. */}
        <PublicLinks />
      </Card>
    </div>
  );
}
